import { useCallback } from "react";
import { graphql, useMutation } from "react-relay";
import {
  AddConnection,
  RemoveSection,
  RemoveTaskConnection,
  UpdateRoutineCreateSectionInput,
  UpdateSectionInput,
 useKPIEditSectionsSaveMutation } from "./__generated__/useKPIEditSectionsSaveMutation.graphql";
import { GoalState } from "./types";
import { KPILocalSection } from "./KPILocalSection";

function getDeleteSections(
  localRemoveSections: string[],
  remoteSectionIds: string[]
): RemoveSection[] {
  return localRemoveSections.flatMap((sectionId) =>
    remoteSectionIds.includes(sectionId) ? [{ sectionId }] : []
  );
}

function getCreateSections(
  localSections: KPILocalSection[],
  remoteSectionIds: string[]
): UpdateRoutineCreateSectionInput[] {
  return localSections.flatMap((ls) =>
    remoteSectionIds.includes(ls.id)
      ? []
      : [{ id: ls.id, teamId: ls.assignedTeam.id, title: ls.title }]
  );
}

function getUpdateSections(
  localSections: KPILocalSection[],
  remoteSectionIds: string[]
): UpdateSectionInput[] {
  return localSections.flatMap((ls) =>
    remoteSectionIds.includes(ls.id)
      ? [
          {
            sectionId: ls.id,
            setTeamId: ls.assignedTeam.id,
            setTitle: ls.title,
          },
        ]
      : []
  );
}

function getCreateConnections(props: {
  sections: KPILocalSection[];
  taskId: string;
  goalState: GoalState;
}): AddConnection[] {
  return props.sections.map((section) => ({
    number: {
      sectionId: section.id,
      lessThan: props.goalState === "less" ? section.goalValue : null,
      greaterThan: props.goalState === "greater" ? section.goalValue : null,
      taskId: props.taskId,
    },
  }));
}

function getRemoveConnections(props: {
  remoteSectionIds: string[];
  taskId: string;
}): RemoveTaskConnection[] {
  return props.remoteSectionIds.map((sectionId) => ({
    sectionId,
    taskId: props.taskId,
  }));
}

export function useKPIEditSectionsSave(props: {
  sections: KPILocalSection[];
  localSections: KPILocalSection[];
  localRemoveSections: string[];
  remoteSectionIds: string[];
  routineId: string;
  taskId: string;
  goalState: GoalState;
  onCompleted(): void;
}) {
  const [saveKPISections, loading] =
    useMutation<useKPIEditSectionsSaveMutation>(graphql`
      mutation useKPIEditSectionsSaveMutation(
        $routineId: ID!
        $input: UpdateRoutineInput!
      ) {
        routine(id: $routineId) {
          update(input: $input) {
            routine {
              id
            }
          }
        }
      }
    `);

  const onSave = useCallback(() => {
    const input = {
      deleteSections: getDeleteSections(
        props.localRemoveSections,
        props.remoteSectionIds
      ),
      createSections: getCreateSections(
        props.localSections,
        props.remoteSectionIds
      ),
      updateSections: getUpdateSections(
        props.localSections,
        props.remoteSectionIds
      ),
      createConnections: getCreateConnections({
        sections: props.sections,
        goalState: props.goalState,
        taskId: props.taskId,
      }),
      removeConnections: getRemoveConnections({
        remoteSectionIds: props.remoteSectionIds,
        taskId: props.taskId,
      }),
    };
    saveKPISections({
      variables: {
        routineId: props.routineId,
        input,
      },
      onCompleted: () => {
        props.onCompleted();
      },
      onError: (e) => {
        console.log(`onError ${JSON.stringify(e, null, 2)}`);
      },
    });
  }, [props, saveKPISections]);

  return [onSave, loading] as const;
}
